import AddRoundedIcon from "@mui/icons-material/Add";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

import Button, { ButtonTypes } from "../../../../components/Button";
import { ErrorState } from "../../../../components/States";
import { useResponsive } from "../../../../hooks/useResponsive";
import { getNumberFormat } from "../../../../utils";
import { BREAKPOINTS } from "../../../../utils/constants";
import {
  Balance,
  Divider,
  EmptyCard,
  EmptyContent,
  EmptyDescription,
  EmptyIcon,
  EmptyText,
  EmptyTitle,
  HeroCard,
  PeriodLabel,
  Stat,
  StatLabel,
  StatsRow,
  StatValue,
  TopBar,
} from "./styles";
import { HeroTone, TransactionsHeroProps } from "./types";

const formatSigned = (value: number, forcedSign?: "+" | "-"): string => {
  const amount = `${getNumberFormat(Math.abs(value))} €`;
  if (forcedSign) {
    return `${forcedSign}${amount}`;
  }
  if (value > 0) {
    return `+${amount}`;
  }
  if (value < 0) {
    return `-${amount}`;
  }
  return amount;
};

const getTone = (value: number): HeroTone => {
  if (value > 0) {
    return "positive";
  }
  if (value < 0) {
    return "negative";
  }
  return "neutral";
};

const TransactionsHero: React.FC<TransactionsHeroProps> = ({
  periodLabel,
  totals,
  error,
  onAddTransaction,
  onResetPeriod,
  onRetry,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const hasTransactions = totals.count > 0;
  const countLabel = isCompact ? "Count" : "Transactions";

  const addButton = (
    <Button
      type={ButtonTypes.primary}
      size="medium"
      onClick={onAddTransaction}
      aria-label="Add transaction"
    >
      <AddRoundedIcon fontSize="small" />
      Add transaction
    </Button>
  );

  const summary = (
    <>
      <Balance $tone={getTone(totals.net)}>{formatSigned(totals.net)}</Balance>
      <Divider />
      <StatsRow>
        <Stat>
          <StatLabel>Income</StatLabel>
          <StatValue $tone="positive">
            {formatSigned(totals.income, "+")}
          </StatValue>
        </Stat>
        <Stat>
          <StatLabel>Expenses</StatLabel>
          <StatValue $tone="negative">
            {formatSigned(totals.expense, "-")}
          </StatValue>
        </Stat>
        <Stat>
          <StatLabel>{countLabel}</StatLabel>
          <StatValue $tone="neutral">{totals.count}</StatValue>
        </Stat>
      </StatsRow>
    </>
  );

  const emptyState = (
    <EmptyCard>
      <EmptyContent>
        <EmptyIcon>
          <ReceiptLongOutlinedIcon />
        </EmptyIcon>
        <EmptyText>
          <EmptyTitle>No transactions in {periodLabel}</EmptyTitle>
          <EmptyDescription>
            Try widening the date range, or clear active filters to see more
            activity.
          </EmptyDescription>
        </EmptyText>
      </EmptyContent>
      <Button
        type={ButtonTypes.cancel}
        onClick={onResetPeriod}
        aria-label="Reset period"
      >
        Reset period
      </Button>
    </EmptyCard>
  );

  const errorState = <ErrorState message={error ?? ""} onRetry={onRetry} />;

  let content = emptyState;
  if (error) {
    content = errorState;
  } else if (hasTransactions) {
    content = summary;
  }

  const desktopAddButton = isCompact ? null : addButton;

  return (
    <HeroCard>
      <TopBar>
        <PeriodLabel>{periodLabel}</PeriodLabel>
        {desktopAddButton}
      </TopBar>
      {content}
    </HeroCard>
  );
};

export default TransactionsHero;
